// Top-of-map tools of the Striping Plan tab (spec 2026-10-14 Part B). Mounted only
// while the tab is active, so its keyboard shortcuts are scoped to it.
import React, { useEffect, useRef } from 'react';

const isTypingTarget = (t) =>
  Boolean(t) && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.tagName === 'SELECT' || t.isContentEditable);

/** Ctrl/⌘+Z undo; Ctrl/⌘+Y or Ctrl/⌘+Shift+Z redo; nothing while typing in a field. */
export const stripingShortcut = (e) => {
  if (!(e.ctrlKey || e.metaKey) || isTypingTarget(e.target)) return null;
  const key = String(e.key || '').toLowerCase();
  if (key === 'z') return e.shiftKey ? 'redo' : 'undo';
  if (key === 'y') return 'redo';
  return null;
};

export default function StripingToolbar({ editor }) {
  const fileRef = useRef(null);
  const editorRef = useRef(editor);
  editorRef.current = editor;

  useEffect(() => {
    const onKey = (e) => {
      const action = stripingShortcut(e);
      if (!action) return;
      e.preventDefault();
      if (action === 'undo') editorRef.current.undo();
      else editorRef.current.redo();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const hasRoute = Boolean(editor.pathId);
  const count = editor.rows.length;
  const btn = 'path-map-intersection-tool-btn';
  return (
    <div className="path-map-intersection-tools striping-map-tools" role="group" aria-label="Striping Plan tools">
      <button
        type="button"
        className={`${btn}${editor.addMode ? ` ${btn}--active` : ''}`}
        aria-pressed={editor.addMode}
        disabled={!hasRoute}
        onClick={() => editor.setAddMode(!editor.addMode)}
        title="Click on the map to add intersections"
      >
        <i className="fa-solid fa-location-dot" aria-hidden />
        <span>Add points</span>
      </button>
      <button type="button" className={btn} disabled={!editor.canUndo} onClick={() => editor.undo()} title="Undo (Ctrl+Z)">
        <i className="fa-solid fa-rotate-left" aria-hidden />
        <span>Undo</span>
      </button>
      <button type="button" className={btn} disabled={!editor.canRedo} onClick={() => editor.redo()} title="Redo (Ctrl+Y)">
        <i className="fa-solid fa-rotate-right" aria-hidden />
        <span>Redo</span>
      </button>
      <button
        type="button"
        className={btn}
        disabled={!hasRoute}
        onClick={() => fileRef.current?.click()}
        title="Import a DOT intersection shapefile (.zip)"
      >
        <i className="fa-solid fa-file-import" aria-hidden />
        <span>Import DOT file</span>
      </button>
      <input
        ref={fileRef}
        type="file"
        accept=".zip,application/zip"
        hidden
        data-testid="striping-dot-input"
        onChange={(e) => {
          const file = e.target.files?.[0];
          e.target.value = '';
          if (file) editor.importFile(file);
        }}
      />
      <button
        type="button"
        className={btn}
        disabled={!count}
        onClick={() => {
          if (window.confirm(
            count === 1 ? 'Remove the 1 intersection of this route?' : `Remove all ${count} intersections of this route?`,
          )) editor.clearAll();
        }}
        title="Remove every intersection of this route"
      >
        <i className="fa-solid fa-trash" aria-hidden />
        <span>Clear all</span>
      </button>
    </div>
  );
}
